import type { CartItem } from '../../reducers/checkout.reducer';
import {
	booleanAttribute,
	ChangeDetectionStrategy,
	Component,
	computed,
	HostListener,
	inject,
	input,
	output,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { RootReducer, Store } from '@app/app.reducers';
import { getFileByKey } from '@app/shared/reducers/file.reducer';
import { combineLatest, distinctUntilChanged, map, of, switchMap, timer } from 'rxjs';

/**
 * Component for displaying retry and change buttons for printfile processing.
 */
@Component({
	selector: 'g-printfile-status-buttons',
	imports: [MatButton, RouterModule],
	templateUrl: './printfile-status-buttons.component.html',
	styleUrl: './printfile-status-buttons.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrintfileStatusButtonsComponent {
	@HostListener('click', ['$event'])
	@HostListener('keyup.enter', ['$event'])
	onHostClick(event: Event | KeyboardEvent): void {
		event.stopPropagation();
	}

	readonly allowRetry$ = input(false, { transform: booleanAttribute, alias: 'allowRetry' });
	readonly cartItem$ = input.required<CartItem>({ alias: 'cartItem' });
	readonly retryPreflight = output<Event>();

	private readonly store = inject(Store<RootReducer.State>);

	readonly fileKey$ = computed(() => {
		const item = this.cartItem$();
		return item.options.printfile?.key;
	});

	readonly shouldShowRetry$ = toSignal(
		combineLatest([
			toObservable(this.allowRetry$),
			toObservable(this.fileKey$).pipe(
				switchMap((fileKey) => (fileKey ? this.store.select(getFileByKey(fileKey)) : of(undefined))),
			),
		]).pipe(
			switchMap(([allow, file]) => {
				if (!allow) return of(false);
				const job = file?.preflightState?.jobState;
				if (!job || job.error?.isRetryable === false) return of(false);

				const canRetryAt = job.canRetryAt;
				if (!canRetryAt) return of(false);

				// File service uses seconds instead of ms.
				const msUntilRetryAllowed = canRetryAt * 1000 - Date.now();
				if (msUntilRetryAllowed <= 0) {
					return of(true);
				}

				return timer(msUntilRetryAllowed).pipe(map(() => true));
			}),
			distinctUntilChanged(),
		),
		{ initialValue: false },
	);
}
